'use client'
import { Icon } from '@/components/atoms/icon/Icon'
import { Col } from '@/components/objects/Col'
import { Flex } from '@/components/objects/Flex'
import { Row } from '@/components/objects/Row'
import { classNames } from '@/components/shared/classNames'
import Link from 'next/link'
import React, { ReactElement, useEffect, useState } from 'react'
import LogoImage from './LogoImage'
import styles from './Sidebar.module.scss'
import { usePathname } from 'next/navigation'

export interface MenuItemData {
    id?: string
    parentID?: string
    label?: string | React.ReactNode | JSX.Element
    url?: string
    className?: string
    submenuList?: MenuItemData[]
    /** Sub-items shown indented under the label; links navigate directly (no slide submenu). */
    inlineSubmenu?: boolean
}

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    mainAction?: ReactElement
}

export const DEFAULT_APPLICATIONS_PATH = '/applications/wioa-imap'
/** @deprecated Use DEFAULT_APPLICATIONS_PATH */
export const DEFAULT_APPLICATIONS_TEST_PATH = DEFAULT_APPLICATIONS_PATH

const menuItems: MenuItemData[] = [
    {
        label: 'Applications',
        inlineSubmenu: true,
        submenuList: [
            {
                url: '/applications/wioa-imap',
                label: 'WIOA/IMAP'
            },
            {
                url: '/applications/isl',
                label: 'ISL'
            }
        ]
    },
    {
        label: 'Records',
        inlineSubmenu: true,
        submenuList: [
            {
                url: '/records/actions',
                label: 'Actions'
            },
            {
                url: '/records/classes',
                label: 'Classes'
            },
            {
                url: '/records/students',
                label: 'Students'
            }
        ]
    }
]

export const Sidebar = ({ mainAction = undefined, className }: Props) => {
    const currentPage = usePathname()
    const [enableClose, setEnableClose] = useState(false)
    const [menuOpen, setMenuOpen] = useState(true)
    const [isInLevelOne, setIsInLevelOne] = useState(true)
    const [isInLevelTwo, setIsInLevelTwo] = useState(false)
    const [isInLevelThree, setIsInLevelThree] = useState(false)
    const [isInLevelFour, setIsInLevelFour] = useState(false)
    const [layerTwoItems, setLayerTwoItems] = useState<MenuItemData[]>([])
    const [layerThreeItems, setLayerThreeItems] = useState<MenuItemData[]>([])
    const [layerFourItems, setLayerFourItems] = useState<MenuItemData[]>([])

    const closeMenu = () => {
        if (enableClose) {
            setMenuOpen(false)
        }
        setLayerTwoItems([])
        setLayerThreeItems([])
        setLayerFourItems([])
    }
    // Si no hay elementos en layerTwoItems, entonces estamos en el primer nivel
    useEffect(() => {
        if (layerTwoItems.length === 0) {
            setIsInLevelOne(true)
        } else {
            setIsInLevelOne(false)
        }
    }, [layerTwoItems])
    // Si hay elementos en layerTwoItems y no hay elementos en layerThreeItems ni en layerFourItems, entonces estamos en el segundo nivel
    useEffect(() => {
        if (layerTwoItems.length > 0 && layerThreeItems.length === 0 && layerFourItems.length === 0) {
            setIsInLevelTwo(true)
        } else {
            setIsInLevelTwo(false)
        }
    }, [layerTwoItems, layerThreeItems, layerFourItems])
    // Si hay elementos en layerThreeItems y no hay elementos en layerFourItems, entonces estamos en el tercer nivel
    useEffect(() => {
        if (layerThreeItems.length > 0 && layerFourItems.length === 0) {
            setIsInLevelThree(true)
        } else {
            setIsInLevelThree(false)
        }
    }, [layerThreeItems, layerFourItems])
    // Si hay elementos en layerFourItems, entonces estamos en el cuarto nivel
    useEffect(() => {
        if (layerFourItems.length > 0) {
            setIsInLevelFour(true)
        } else {
            setIsInLevelFour(false)
        }
    }, [layerFourItems])

    useEffect(() => {
        if (currentPage.startsWith('/applications') || currentPage.startsWith('/records')) {
            setLayerTwoItems([])
            setLayerThreeItems([])
            setLayerFourItems([])
        }
    }, [currentPage])

    const goBackwards = () => {
        if (isInLevelFour) {
            setLayerFourItems([])
        } else if (isInLevelThree) {
            setLayerThreeItems([])
        } else if (isInLevelTwo) {
            setLayerTwoItems([])
        } else {
            closeMenu()
        }
    }

    const subItemsIcon = <Icon name="chevronRight" className="w-4 h-4 absolute right-3" />
    const extraProps = {
        onClick: () => {
            closeMenu()
        }
    }
    const MainActionElement = mainAction ? React.cloneElement(mainAction, extraProps) : undefined

    return (
        <Flex className={classNames('relative', className)}>
            <Col
                className={classNames(
                    'fixed inset-0 bg-white z-50 w-72 h-auto max-w-[max-content]',
                    styles['menu-layer'],
                    styles['slide-from-left-animation'],
                    menuOpen ? styles['slide-from-left-animation--active'] : ''
                )}
            >
                <Row className="items-center justify-center h-[150px]">
                    {!isInLevelOne && (
                        <div
                            className="flex items-center text-primary text-2xl font-bold underline underline-offset-8 decoration-primary-lightest absolute left-5 top-5 group"
                            onClick={goBackwards}
                        >
                            <Icon name="arrowLeft" className="w-5 h-5 text-primary-dark group-hover:-translate-x-1 duration-300" />
                        </div>
                    )}
                    {MainActionElement}
                    <LogoImage variant="short" />
                </Row>
                <Col className="max-h-[100vh-150px] h-[70vh] overflow-y-auto px-3 pt-5">
                    {isInLevelOne && (
                        <div
                            id="layerOne"
                            className={classNames(
                                styles['items-wrapper'],
                                styles['slide-from-left-animation'],
                                isInLevelOne ? styles['slide-from-left-animation--active'] : ''
                            )}
                        >
                            {menuItems.map((menuItem, index) => {
                                if (
                                    menuItem.inlineSubmenu &&
                                    menuItem.submenuList &&
                                    menuItem.submenuList.length > 0
                                ) {
                                    const isSectionActive = menuItem.submenuList.some(
                                        sub => sub.url && currentPage.startsWith(String(sub.url))
                                    )
                                    return (
                                        <div key={index} className={styles['menu-group']}>
                                            <div
                                                className={classNames(
                                                    styles['menu-group-title'],
                                                    isSectionActive ? styles.active : ''
                                                )}
                                            >
                                                {menuItem.label}
                                            </div>
                                            {menuItem.submenuList.map((subItem, subIndex) =>
                                                subItem.url ? (
                                                    <Link
                                                        href={String(subItem.url)}
                                                        key={subIndex}
                                                        legacyBehavior
                                                    >
                                                        <a
                                                            className={classNames(
                                                                styles['menu-item'],
                                                                styles['menu-subitem'],
                                                                'link',
                                                                currentPage.startsWith(String(subItem.url))
                                                                    ? styles.active
                                                                    : ''
                                                            )}
                                                        >
                                                            {subItem.label}
                                                        </a>
                                                    </Link>
                                                ) : null
                                            )}
                                        </div>
                                    )
                                }

                                return menuItem.submenuList && menuItem.submenuList?.length > 0 ? (
                                    <div
                                        key={index}
                                        className={classNames(styles['menu-item'], currentPage === menuItem.url ? styles.active : '')}
                                        onClick={() => {
                                            setLayerTwoItems(menuItem.submenuList ?? [])
                                        }}
                                    >
                                        {menuItem.label} {subItemsIcon}
                                    </div>
                                ) : menuItem.url ? (
                                    <Link href={String(menuItem.url)} key={index} legacyBehavior>
                                        <a
                                            className={classNames(
                                                styles['menu-item'],
                                                'link',
                                                currentPage === menuItem.url ? styles.active : ''
                                            )}
                                            onClick={closeMenu}
                                        >
                                            {menuItem.label}
                                            <i
                                                className={classNames(
                                                    styles['icon-active'],
                                                    styles['active--icon-active'],
                                                    'fa-solid fa-angle-right'
                                                )}
                                                aria-hidden="true"
                                            ></i>
                                        </a>
                                    </Link>
                                ) : (
                                    <div key={index} className={classNames(styles['menu-item'], 'link')}>
                                        {menuItem.label}
                                        <i
                                            className={classNames(
                                                styles['icon-active'],
                                                styles['active--icon-active'],
                                                'fa-solid fa-angle-right'
                                            )}
                                            aria-hidden="true"
                                        ></i>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    {isInLevelTwo && (
                        <div
                            id="layerTwo"
                            className={classNames(
                                styles['items-wrapper'],
                                styles['slide-from-left-animation'],
                                isInLevelTwo ? styles['slide-from-left-animation--active'] : '',
                                'absolute'
                            )}
                        >
                            {layerTwoItems.map((menuItem, index) => {
                                return menuItem.submenuList && menuItem.submenuList?.length > 0 ? (
                                    <div
                                        key={index}
                                        className={classNames(
                                            styles['menu-item'],
                                            menuItem.url && currentPage.startsWith(menuItem.url) ? styles.active : ''
                                        )}
                                        onClick={() => {
                                            setLayerThreeItems(menuItem.submenuList ?? [])
                                        }}
                                    >
                                        {menuItem.label} {subItemsIcon}
                                    </div>
                                ) : (
                                    <Link href={String(menuItem.url)} key={index} legacyBehavior>
                                        <a
                                            className={classNames(
                                                styles['menu-item'],
                                                'link',
                                                menuItem.url && currentPage.startsWith(menuItem.url)
                                                    ? styles.active
                                                    : ''
                                            )}
                                            onClick={closeMenu}
                                        >
                                            {menuItem.label}
                                            <i className={(styles['icon-active'], 'fa-solid fa-angle-right')} aria-hidden="true"></i>
                                        </a>
                                    </Link>
                                )
                            })}
                        </div>
                    )}
                    {isInLevelThree && (
                        <div
                            id="layerThree"
                            className={classNames(
                                styles['items-wrapper'],
                                styles['slide-from-left-animation'],
                                isInLevelThree ? styles['slide-from-left-animation--active'] : '',
                                'absolute'
                            )}
                        >
                            {layerThreeItems.map((menuItem, index) => {
                                return menuItem.submenuList && menuItem.submenuList?.length > 0 ? (
                                    <div
                                        key={index}
                                        className={classNames(styles['menu-item'])}
                                        onClick={() => {
                                            setLayerFourItems(menuItem.submenuList ?? [])
                                        }}
                                    >
                                        {menuItem.label} {subItemsIcon}
                                    </div>
                                ) : (
                                    <Link href={String(menuItem.url)} key={index} legacyBehavior>
                                        <a className={classNames(styles['menu-item'], 'link')} onClick={closeMenu}>
                                            {menuItem.label}
                                        </a>
                                    </Link>
                                )
                            })}
                        </div>
                    )}
                    {isInLevelFour && (
                        <div
                            id="layerFour"
                            className={classNames(
                                styles['items-wrapper'],
                                styles['slide-from-left-animation'],
                                isInLevelFour ? styles['slide-from-left-animation--active'] : '',
                                'absolute'
                            )}
                        >
                            {layerFourItems.map((menuItem, index) => {
                                return menuItem.submenuList && menuItem.submenuList?.length > 0 ? (
                                    <div key={index} className={classNames(styles['menu-item'])}>
                                        {menuItem.label}
                                    </div>
                                ) : (
                                    <Link href={String(menuItem.url)} key={index} legacyBehavior>
                                        <a className={classNames(styles['menu-item'], 'link')} onClick={closeMenu}>
                                            {menuItem.label}
                                        </a>
                                    </Link>
                                )
                            })}
                        </div>
                    )}
                </Col>
            </Col>
        </Flex>
    )
}
